import React, {useEffect, useState} from 'react'
import {Switch, Route, Redirect, withRouter, useLocation} from 'react-router-dom'
import { ToastContainer } from 'components/shared/Toaster'
import LoginPage from 'components/Login/LoginPage'

import './styles/tokens/index.scss'
import 'bootstrap/dist/css/bootstrap.css'
import 'react-toastify/dist/ReactToastify.css'
import HomeComponent from 'components/Home'
import ErrorBoundary from 'ErrorBoundary'
import { useDispatch } from 'react-redux'
import Processing from './components/shared/Processing/Processing'
import { getUserDetailByToken } from './actions/root'
import plaid from 'container/integerations/plaid'
import Auth from 'tools/AuthService'
import './App.scss'
import PlaidOAuthSuccess from "./components/Integerations/Plaid/Success/PlaidOAuthSuccess";
import PlaidSuccess from "./components/Integerations/Plaid/Success/PlaidSuccess";
import InvoiceSubmissionContainer from "./container/InvoiceSubmissionContainer";
import {initializePostHog, captureEvent, startPostHog} from "./posthog/posthogConfig";
import { ContextProvider } from "./context/AppContext";
import ThankYou from 'components/Unauth/ThankYou';
import { ThemeProvider } from '@material-ui/core/styles';
import theme from './theme'
import welcomeSurvey from "./components/WelcomeSurvey/WelcomeSurvey";
import CompleteProfileModal from "./components/CompleteProfile/CompleteProfileModal";

const pendoIntegeration = ({ userId, email, firstName = '', lastName = '', securityContext }) => {
  if (runningInProd()) {
    window.pendo.initialize({
      visitor: {
        id: email,
        email,
        userName: `${firstName} ${lastName}`
      },
      account: {
        id: securityContext.client.clientId,
        name: securityContext.client.clientName
      }
    });
  }
}

const bootIntercomLogIn = ({ userId, email, firstName = '', lastName = '', securityContext }) => {
  if (runningInProd()) {
    window.Intercom('boot', {
      api_base: "https://api-iam.intercom.io",
      app_id: "i65u08km",
      email: email,
      userName: `${firstName} ${lastName}`,
      clientId: securityContext.client.clientId,
      clientName: securityContext.client.clientName
    });
  }
}

function hotjarIntegration({ userId, email, firstName = '', lastName = '', securityContext }) {
  if (runningInProd()) {
    window.hj('identify', userId, {
      name: `${firstName} ${lastName}`,
      email: email,
      client_id: securityContext.client.clientId,
      client_name: securityContext.client.clientName,
    });
  }
}

function runningInProd() {
  const currentUrl = window.location.href;
  return (currentUrl.startsWith("https://app.roxwrite.com" || currentUrl.startsWith("https://appv2.roxwrite.com")))
      && process.env.NODE_ENV === 'production'
}

export function runningInProdOrDemo() {
  const currentUrl = window.location.href;
  return currentUrl.startsWith("https://app.roxwrite.com") || currentUrl.startsWith("https://appv2.roxwrite.com") || currentUrl.startsWith("https://demo.roxwrite.com")
}

function mazeIntegration() {
  if (runningInProd()) {
    window.roxMazeScript(window, document, 'https://snippet.maze.co/maze-universal-loader.js', '6827ffe6-2817-4e87-8424-dd8c9259a726')
  }
}

function postHogIntegration() {
  if (runningInProdOrDemo()) {
    initializePostHog()
  }
}

function App(props) {
  const {
    history
  } = props

  const [userLoggedIn, setUserLoggedIn] = useState(Auth.isLoggedIn())
  const [userInfo, setUserInfo] = useState(null)
  const [profileReminderOpen, setProfileReminderOpen] = useState(false)
  const dispatch = useDispatch()
  const location = useLocation();

  // PostHog has to be live before anything tries to report. posthog.capture/identify are
  // dropped (not queued) when called before init, so initializing this from initUser - after
  // /me resolved - silently discarded every pre-login event: the pageviews on /login and the
  // whole signup funnel. Declared before the pageview effect so React runs it first on mount.
  useEffect(() => {
    postHogIntegration()
  }, []);

  useEffect(() => {
    if (runningInProdOrDemo()) {
      captureEvent('$pageview', userInfo);
    }
  }, [location.pathname, userInfo]);

  useEffect(() => {
    const shouldShowWelcomeSurvey = localStorage.getItem('showWelcomeSurvey') === 'true';
    const isOnWelcomeSurvey = history.location.pathname === '/welcome-survey';
    const isLoginFlow = ['/login', '/thank-you', 'application-form', '/get-started'].some(path =>
        history.location.pathname.includes(path)
    );
    const hasToken = Auth.isLoggedIn();
    if (userLoggedIn && hasToken && shouldShowWelcomeSurvey && !isOnWelcomeSurvey && !isLoginFlow) {
      history.replace('/welcome-survey');
    }
  }, [userLoggedIn, location.pathname]);

  const initUser = (res) => {
    pendoIntegeration(res)
    bootIntercomLogIn(res)
    hotjarIntegration(res)
    mazeIntegration()
    Auth.loggedInUser = res
    setUserInfo(res)
    setUserLoggedIn(true)
    // Pending leads see a dismissable reminder, unless dismissed this session or
    // arriving via a deep link. Read here (not in handleLogin) so both concurrent
    // user fetches honor it and neither re-opens the modal.
    const suppressProfileReminder = sessionStorage.getItem('deepLinkRedirect') === 'true'
    // Customer admins only: the form also edits the company's details
    const isCustomerAdmin = res?.securityContext?.roles?.some(role => role.roleName === 'CUSTOMER_ADMIN')
    if (res?.status === 'Pending' && isCustomerAdmin && !suppressProfileReminder && !sessionStorage.getItem('profileReminderDismissed')) {
      setProfileReminderOpen(true)
    } else {
      setProfileReminderOpen(false)
    }
  }

  const dismissProfileReminder = () => {
    sessionStorage.setItem('profileReminderDismissed', 'true')
    setProfileReminderOpen(false)
  }

  // After a successful complete-registration, re-fetch /v1/user/me (now
  // "Active") so the app state updates and the reminder never shows again.
  const handleProfileCompleted = () => {
    dispatch(getUserDetailByToken()).then(res => {
      Auth.loggedInUser = res
      setUserInfo(res)
      setProfileReminderOpen(false)
    })
  }

  const handleLogin = () => {
    let redirectUrl = localStorage.getItem('redirectUrl')
    let defaultRedirect = '/dashboard'
    // Each fresh login re-evaluates the profile reminder (the dismiss flag lives
    // in sessionStorage and would otherwise survive an in-tab logout/login).
    sessionStorage.removeItem('profileReminderDismissed')
    dispatch(getUserDetailByToken()).then(res => {
      initUser(res)

      // Set default redirect based on user role
      if (res?.securityContext?.roles?.some(role => role.roleName === 'BROKER_CUSTOMER')) {
        defaultRedirect = '/broker-portal'
        redirectUrl = null
      }
      // Sales reps land on their shared applications; a remembered link elsewhere would only redirect there
      if (res?.securityContext?.roles?.some(role => role.roleName === 'SALES_REP')) {
        defaultRedirect = '/applicants'
        if (!redirectUrl?.startsWith('/applicant')) {
          redirectUrl = null
        }
      }

      if (runningInProdOrDemo()){
        startPostHog(res.username)
      }
      history.push(redirectUrl || defaultRedirect)
      localStorage.removeItem('redirectUrl')
    })
  }

  const pathName = window.location.pathname
  if (!['/login', '/thank-you', 'application-form', '/get-started'].some(path => pathName.includes(path))) {
    // Keep the query string so deep links survive the redirect to login.
    localStorage.setItem('redirectUrl', pathName + window.location.search)
  }

  if (!userInfo && Auth.isLoggedIn()) {
    dispatch(getUserDetailByToken()).then(res => {
      initUser(res)
    })
    return null
  }

  const redirectTOLogin = !['passwordRecovery', 'plaid', 'application-form', 'thank-you','confirm-email', 'get-started', 'login/'].some(path => history.location.pathname.includes(path))

  // Already-authenticated user on /login/otp?target=/x: skip OTP, go to the target.
  const loginDeepLinkTarget = (() => {
    if (!userLoggedIn) return null
    const path = history.location.pathname
    if (path !== '/login/otp' && path !== '/login/register-otp') return null
    const raw = (new URLSearchParams(history.location.search).get('target') || '').trim()
    if (!raw.startsWith('/') || raw.startsWith('//') || raw.includes(':')) return null
    return raw
  })()

  return (
    <ErrorBoundary>
      <div className='app'>
      <ContextProvider>
        <ThemeProvider theme={theme}>
          <ToastContainer />
          <Processing />
          <CompleteProfileModal
            open={profileReminderOpen && userLoggedIn}
            user={userInfo}
            onDismiss={dismissProfileReminder}
            onCompleted={handleProfileCompleted}
          />
        {redirectTOLogin && !userLoggedIn ? <Redirect {...props} to={{ pathname: '/login' }} /> : null}
        {loginDeepLinkTarget ? <Redirect to={loginDeepLinkTarget} /> : null}
        <Switch>
          <Route
              exact
              path={['/welcome-survey']}
              component={welcomeSurvey}
          />
          <Route
              exact
              path={['/applications/:applicantUUID/owners/:ownerId/plaid']}
              component={plaid}
          />
          <Route
              exact
              path={['/applications/:applicantUUID/owners/:ownerId/plaid/:custom']}
              component={plaid}
          />
          <Route
              exact
              path={['/plaid-success']}
              component={PlaidOAuthSuccess}
          />
          <Route
              exact
              path={['/plaid-auth-success']}
              component={PlaidSuccess}
          />
          <Route
              exact
              path={['/plaid-auth-success/:custom']}
              component={PlaidSuccess}
          />
          <Route
              exact
              path={['/application-form']}
              component={InvoiceSubmissionContainer}
          />
          <Route
              exact
              path={['/thank-you']}
              component={ThankYou}
          />
          {!userLoggedIn ? (
            <>
            <Route
              exact
              path={['/login', '/login/otp', '/login/register-otp', '/login/forgot-password', '/passwordRecovery/:email/:tokenId','/confirm-email/:confirmationToken', '/get-started']}
              render={(props) => <LoginPage onLogIn={() => handleLogin()} {...props} />}
            />
            <Route
                exact
                path={['/applications/:applicantUUID/owners/:ownerId/plaid/:custom']}
                component={plaid}
            />
            <Route
                exact
                path={['/plaid-success']}
                component={PlaidOAuthSuccess}
            />
            <Route
                exact
                path={['/plaid-auth-success']}
                component={PlaidSuccess}
            />
            <Route
                exact
                path={['/plaid-auth-success/:custom']}
                component={PlaidSuccess}
            />
            <Route
                exact
                path={['/application-form']}
                component={InvoiceSubmissionContainer}
            />
            <Route
                exact
                path={['/thank-you']}
                component={ThankYou}
            />
            </>
            ) : (
              <>
                <Route
                  exact
                  path='/'
                  render={props => (
                    <Redirect {...props} to={{ pathname: Auth.isBrokerCustomer() ? '/broker-portal' : Auth.isSalesRep() ? '/applicants' : '/dashboard' }} />
                  )}
                />
                <HomeComponent />
              </>
            )}
          </Switch>
        </ThemeProvider>
      </ContextProvider>
      </div>
    </ErrorBoundary>
  )
}

export default withRouter(App)
